/**
 * API Configuration
 * Centralized configuration for all API endpoints
 * Updated: 2026-07-15
 */

// Get the base API URL from environment variables with fallback
const getApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;

  // Fallback to localhost for development if not set to
  if (!envUrl) {
    console.warn('REACT_APP_API_BASE_URL not set, using localhost fallback');
    return 'http://localhost:5000';
  }
  return envUrl;
};

// Base API URL for existing backend services
export const API_BASE_URL = getApiBaseUrl();

// AI Services run on local Flask backend during development
const AI_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Automation Insights — topsreliabilityframework-automationapi, this
// platform's own dedicated service (cutover 2026-08-25; previously the
// legacy standalone Automation Dashboard backend called via a second,
// independently-scoped token — see PHASE_11_BACKEND_MIGRATION_PLAN.md §2.1/§6.4).
// Authenticated with the platform's normal sign-in token (services/authFetch.ts).
// No localhost fallback — REACT_APP_AUTOMATION_API_BASE_URL must be set (see .env locally, Vault frontendsecrets in deployed envs).
const getAutomationApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_AUTOMATION_API_BASE_URL;
  if (!envUrl) {
    throw new Error('REACT_APP_AUTOMATION_API_BASE_URL is not set');
  }
  return envUrl;
};

const AUTOMATION_API_BASE_URL = getAutomationApiBaseUrl();

// API Endpoints Configuration
export const API_ENDPOINTS = {
  // Reliability Framework
  RELIABILITY_FRAMEWORK: {
    PORTFOLIO_OVERVIEW: `${API_BASE_URL}/api/reliability-framework/portfolio-overview`,
    PARAMETERS: `${API_BASE_URL}/api/reliability-framework/parameters`,
    ALL_APPS: `${API_BASE_URL}/api/reliability-framework/portfolio-overview`,
    FULLSTACK_MONITORING_PROGRESS: `${API_BASE_URL}/api/reliability-framework/fullstack-monitoring-progress`, // NEW: crawl/walk/run/monitoring progress
    PLATFORM_PENDING_ITEMS: `${API_BASE_URL}/api/reliability-framework/platform-pending-items`, // NEW: reliability action items by platform
  },

  // Resiliency MSA Assessment
  RESILIENCY_MSA: {
    ALL: `${API_BASE_URL}/api/resiliency-msa`,
    BY_APP: (appName: string) => `${API_BASE_URL}/api/resiliency-msa?applicationName=${encodeURIComponent(appName)}`,
  },

  // Resiliency Framework — commented out, replaced by Resiliency MSA Assessment
  // RESILIENCY_FRAMEWORK: {
  //   PORTFOLIO_OVERVIEW: `${API_BASE_URL}/api/resiliency-framework/portfolio-overview`,
  //   PARAMETERS: `${API_BASE_URL}/api/resiliency-framework/parameters`,
  //   UPDATE_PARAMETER: `${API_BASE_URL}/api/resiliency-framework/update-parameter`,
  //   FULLSTACK_RESILIENCY_PROGRESS: `${API_BASE_URL}/api/resiliency-framework/fullstack-resiliency-progress`,
  //   APPLICATION: (referenceId: string, parameterName?: string) =>
  //     `${API_BASE_URL}/api/resiliency-framework/application/${referenceId}${parameterName ? `?parameter_name=${parameterName}` : ''}`,
  //   AUDIT_HISTORY: (referenceId: string, parameterName?: string) =>
  //     `${API_BASE_URL}/api/resiliency-framework/audit-history/${referenceId}${parameterName ? `?parameter_name=${parameterName}` : ''}`,
  // },

  // Parameter Links
  PARAMETER_LINKS: (referenceId: string) => `${API_BASE_URL}/api/parameter-links/${referenceId}`,

  // Parameter Updates
  UPDATE_PARAMETER: `${API_BASE_URL}/api/update-parameter`,

  // Audit History
  AUDIT_HISTORY: (referenceId: string) => `${API_BASE_URL}/api/reliability-framework/audit-history/${referenceId}`,
  CREATE_AUDIT_ENTRY: `${API_BASE_URL}/api/reliability-framework/audit-history`,

  // Incidents
  INCIDENTS: {
    BASE: `${API_BASE_URL}/api/incidents`,
    P1_P2: `${API_BASE_URL}/api/p1-p2-incidents`,
    P3_P4: `${API_BASE_URL}/api/p3-p4-incidents`,
    P1_P2_WITH_TRENDS: `${API_BASE_URL}/api/p1-p2-incidents-with-trends`,
    P3_P4_WITH_TRENDS: `${API_BASE_URL}/api/p3-p4-incidents-with-trends`,
    MTTR: `${API_BASE_URL}/api/inc-mttr`,
    P1P2_BY_REGION: `${API_BASE_URL}/api/p1p2-by-region`,
  },

  // Hedwig Analysis
  HEDWIG: {
    COMPREHENSIVE_ANALYSIS: `${API_BASE_URL}/api/hedwig/comprehensive-analysis`,
  },

  // AI Analysis (Local Flask Backend)
  AI: {
    ANALYZE_ALERTS: `${AI_BASE_URL}/api/ai/analyze-alerts`,
    HEALTH: `${AI_BASE_URL}/api/ai/health`,
    CONFIG: `${AI_BASE_URL}/api/ai/config`,
  },

  // SNR Alert Insights
  SNR: {
    ALERT_INSIGHTS: `${API_BASE_URL}/api/snr/alert-insights`,
    ALERT_INSIGHTS_BY_APP: (appId: string) => `${API_BASE_URL}/api/snr/alert-insights/${encodeURIComponent(appId)}`,
  },

  // Pre-Incident Correlation Analysis
  PRE_INCIDENT_CORRELATION: {
    SUMMARY: `${API_BASE_URL}/api/pre-incident-correlation/summary`,
    INCIDENTS: `${API_BASE_URL}/api/pre-incident-correlation/incidents`,
    FILTER_OPTIONS: `${API_BASE_URL}/api/pre-incident-correlation/filter-options`,
    WEEKLY_TREND: `${API_BASE_URL}/api/pre-incident-correlation/weekly-trend`,
    INCIDENT_DETAIL: (incidentNumber: string) =>
      `${API_BASE_URL}/api/pre-incident-correlation/incidents/${encodeURIComponent(incidentNumber)}`,
    INCIDENT_LIVE_ALERTS: (incidentNumber: string) =>
      `${API_BASE_URL}/api/pre-incident-correlation/incidents/${encodeURIComponent(incidentNumber)}/live-alerts`,
  },

  // Portfolio Health
  PORTFOLIO_HEALTH: {
    ALL_APPS_SLO_INFO: `${API_BASE_URL}/api/fetch-all-apps-slo-info`,
    NFR_TEST_INSIGHTS: `${API_BASE_URL}/api/get-ipace-data`,
  },

  // DCO / Decommissioning Cost
  DCO: {
    LOAD_CSV: `${API_BASE_URL}/api/reliability-framework/dco/load-csv`,
    CANCEL_LOAD: `${API_BASE_URL}/api/reliability-framework/dco/cancel-load`,
  },

  // Operational Excellence - Postmortem Actions
  OPERATIONAL_EXCELLENCE: {
    POSTMORTEM_ACTIONS: {
      SUMMARY: `${API_BASE_URL}/api/operational-excellence/postmortem-actions/summary`,
      BY_APPLICATION: `${API_BASE_URL}/api/operational-excellence/postmortem-actions/by-application`,
      LIST: `${API_BASE_URL}/api/operational-excellence/postmortem-actions`,
    },
    POSTMORTEM_STATUS: {
      SUMMARY: `${API_BASE_URL}/api/operational-excellence/postmortem-status/summary`,
    },
  },

  // Jira Integration
  JIRA: {
    HIERARCHY: `${API_BASE_URL}/api/jira/hierarchy`,
  },

  // Postmortem APIs (Maersk API Gateway)
  POSTMORTEM: {
    // Frontend should call backend proxy endpoints (backend will fetch from Maersk)
    OAUTH_TOKEN: `${API_BASE_URL}/api/postmortem/oauth-token`,
    ALL_REPORTS: `${API_BASE_URL}/api/postmortem/all`,
    ENHANCEMENTS: `${API_BASE_URL}/api/postmortem/enhancements`,
  },

  // SpringPM Proxy (DEPRECATED - Use POSTMORTEM instead)
  SPRINGPM: {
    ALL_REPORTS: `${API_BASE_URL}/api/proxy/springpm-all`,
  },

  // SRE Page 0 Metrics
  PAGE0_METRICS: {
    WEEKLY: `${API_BASE_URL}/api/page0-metrics`,
  },

  // MVC App View
  MVC_VIEW: {
    APPLICATIONS: `${API_BASE_URL}/api/mvc-view/applications`,
    APPLICATION: (appId: string) => `${API_BASE_URL}/api/mvc-view/application/${appId}`,
    PORTFOLIOS: `${API_BASE_URL}/api/mvc-view/portfolios`,
    PLATFORMS: `${API_BASE_URL}/api/mvc-view/platforms`,
    PLATFORM_PORTFOLIOS: `${API_BASE_URL}/api/mvc-view/platform-portfolios`,
    STATISTICS: `${API_BASE_URL}/api/mvc-view/statistics`,
  },

  // KPI Targets & Forecast (Incident Ops View)
  KPI_TARGETS: {
    BASE: `${API_BASE_URL}/api/kpi-targets`,
    BASE_PUT: `${API_BASE_URL}/api/kpi-targets/base`,
    OVERRIDE: `${API_BASE_URL}/api/kpi-targets/override`,
    AUDIT: `${API_BASE_URL}/api/kpi-targets/audit`,
    OVERRIDE_DELETE: (metricId: string, periodType: string, periodKey: string, changedBy?: string) =>
      `${API_BASE_URL}/api/kpi-targets/override/${encodeURIComponent(metricId)}/${encodeURIComponent(periodType)}/${encodeURIComponent(periodKey)}${changedBy ? `?changed_by=${encodeURIComponent(changedBy)}` : ''}`,
  },
} as const;

// Automation Insights — kept in its own const, not spread into API_ENDPOINTS above,
// because it targets AUTOMATION_API_BASE_URL instead of API_BASE_URL.
// Endpoint paths/params match topsreliabilityframework-automationapi.
export const AUTOMATION_INSIGHTS_ENDPOINTS = {
  BASE_URL: AUTOMATION_API_BASE_URL,
  PANELS: {
    PERFORMANCE_ALERTS: `${AUTOMATION_API_BASE_URL}/automation/api/panels/performance-alerts`,
    PLATFORM_SUMMARY: `${AUTOMATION_API_BASE_URL}/automation/api/panels/platform-table`,
    PLATFORM_PERFORMANCE: `${AUTOMATION_API_BASE_URL}/automation/api/panels/platform-summary`,
    // Assignment-group drill-down (Stage 4, Platform View only) — added 2026-09-11.
    ASSIGNMENT_GROUPS: `${AUTOMATION_API_BASE_URL}/automation/api/panels/assignment-groups`,
    PLATFORM_TABLE_GROUPS: `${AUTOMATION_API_BASE_URL}/automation/api/panels/platform-table/groups`,
    AUTOMATION_BREAKDOWN: `${AUTOMATION_API_BASE_URL}/automation/api/panels/automation-breakdown`,
    PLATFORM_TABLE_TICKETS: `${AUTOMATION_API_BASE_URL}/automation/api/panels/platform-table/tickets`,
  },
  DATA: `${AUTOMATION_API_BASE_URL}/automation/api/data`,
  // Page-visit tracking / leaderboard — added 2026-08-25, isolated feature
  // (see services/visitTrackingService.ts + routers/visits.py in the backend).
  VISITS: {
    PING: `${AUTOMATION_API_BASE_URL}/automation/api/visits/ping`,
    // Active-time heartbeat — only sent while the tab is visible + focused.
    // Added 2026-09-11 alongside the accurate active-time tracking rework.
    BEAT: `${AUTOMATION_API_BASE_URL}/automation/api/visits/beat`,
    LEADERBOARD: `${AUTOMATION_API_BASE_URL}/automation/api/visits/leaderboard`,
  },
} as const;

// SAP Insights POC — dev-only, fully isolated from AUTOMATION_INSIGHTS_ENDPOINTS
// above (same backend service/base URL, different router paths — see
// routers/automation.py's /api/panels/sap-platform-table(/groups) routes).
export const SAP_INSIGHTS_ENDPOINTS = {
  BASE_URL: AUTOMATION_API_BASE_URL,
  PANELS: {
    PLATFORM_TABLE: `${AUTOMATION_API_BASE_URL}/automation/api/panels/sap-platform-table`,
    PLATFORM_TABLE_GROUPS: `${AUTOMATION_API_BASE_URL}/automation/api/panels/sap-platform-table/groups`,
    PLATFORM_TABLE_TICKETS: `${AUTOMATION_API_BASE_URL}/automation/api/panels/sap-platform-table/tickets`,
  },
} as const;

// Export individual endpoints for convenience
export const {
  RELIABILITY_FRAMEWORK,
  // RESILIENCY_FRAMEWORK,  // Commented out — replaced by Resiliency MSA Assessment
  RESILIENCY_MSA,
  PARAMETER_LINKS,
  UPDATE_PARAMETER,
  INCIDENTS,
  HEDWIG,
  SNR,
  AI,
  PORTFOLIO_HEALTH,
  DCO,
  OPERATIONAL_EXCELLENCE,
  JIRA,
  POSTMORTEM,
  SPRINGPM,
  MVC_VIEW,
  KPI_TARGETS,
} = API_ENDPOINTS;

// Log the current API configuration in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Configuration loaded:', {
    baseUrl: API_BASE_URL,
    aiBaseUrl: AI_BASE_URL,
    environment: process.env.NODE_ENV,
  });
}
